import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://aoucmkkqfbqccmcerwtr.supabase.co'
// ВАЖНО: для аутентификации supabase-js должен использовать ANON key
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const useWorkerProxy = (import.meta.env.VITE_USE_WORKER_PROXY || 'false') === 'true'

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables. Please check your .env file.')
  console.error('Required variables: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY')
}

// Вспомогательные утилиты для синхронизации JWT в shared storage (читается worker-клиентом)
const extractProjectRef = (url: string): string | null => {
  try {
    const { hostname } = new URL(url);
    const [ref] = hostname.split('.') as [string, ...string[]];
    return ref || null;
  } catch {
    return null;
  }
};
const PROJECT_REF = extractProjectRef(supabaseUrl);
// IMPORTANT: Do NOT overwrite Supabase's own sb-<ref>-auth-token structure.
// Use a separate key for any cross-client/worker token mirroring.
const WORKER_AUTH_STORAGE_KEY = PROJECT_REF ? `ap-worker-auth-token-${PROJECT_REF}` : undefined;

// Безопасное кодирование в стандартный base64 для разных окружений (Browser/Node)
const base64EncodeForBoth = (input: string): string => {
  if (typeof window === 'undefined') {
    // Node / SSR
    return Buffer.from(input, 'utf8').toString('base64')
  }
  // Browser: корректная обработка Unicode
  return btoa(unescape(encodeURIComponent(input)))
}

// Realtime apikey: ВСЕГДА передаем в base64 (ожидание текущего Realtime-сервера)
// Это устраняет ошибки вида "incorrect padding" при разборе apikey на стороне Realtime
const realtimeApiKey = base64EncodeForBoth(supabaseAnonKey as string)

// Используем ANON key для корректной работы аутентификации
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Добавляем explicit timeout settings по рекомендации Supabase
    flowType: 'pkce',
    debug: import.meta.env.DEV,
  },
  global: {
    // Добавляем глобальные настройки timeout
    headers: {
      'X-Client-Info': 'arendapro-main-client',
    },
    // fetch: удален для диагностики
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
      // Используем Phoenix v2 протокол для Realtime
      vsn: '2.0.0',
      // Явно указываем tenant
      project: 'api',
    }
  }
})

// Инициализация Realtime токена при старте приложения (до первых подписок)
;(async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token ?? null
    supabase.realtime.setAuth(token ?? undefined)
  } catch (e) {
    console.warn('[supabase client] Failed to set initial realtime auth token', e)
  }
})()


// ИСПРАВЛЕНО: Упрощенный rate limiting без циклической зависимости на auth
export const withRateLimit = async <T>(
  operation: () => Promise<T>,
  action: string = 'api_request'
): Promise<T> => {
  // ВРЕМЕННО ОТКЛЮЧЕНО: Убираем вызов supabase.auth.getUser() чтобы предотвратить зависание
  // Это устраняет циклическую зависимость: AuthContext → getSession → withRateLimit → getUser
  
  console.log('withRateLimit: Executing operation without auth-based rate limiting (temporary fix)');
  
  // Простая защита от спама без auth dependency
  if (typeof window !== 'undefined') {
    const now = Date.now();
    const lastCall = window.sessionStorage.getItem(`rate_limit_${action}`);
    
    if (lastCall && (now - parseInt(lastCall)) < 100) { // 100ms минимальный интервал
      throw new Error('Rate limit: Too many requests');
    }
    
    window.sessionStorage.setItem(`rate_limit_${action}`, now.toString());
  }
  
  return operation();
};

// Enhanced authentication with rate limiting
export const signInWithRateLimit = async (email: string, password: string) => {
  // TEMPORARY FIX: Disable apiRateLimiter due to Supabase table issues
  console.log('[signInWithRateLimit] DISABLED - allowing login for:', email);
  
  return supabase.auth.signInWithPassword({ email, password });
};

// Синхронизация JWT токена в shared storage для supabaseWorker
// Это обеспечивает единый контекст авторизации между клиентами
try {
  supabase.auth.onAuthStateChange((_event, session) => {
    try {
      if (typeof window === 'undefined' || !WORKER_AUTH_STORAGE_KEY) return;
      // Mirror token to a dedicated worker storage key only. Never touch the official sb-* key here.
      if (session?.access_token) {
        const payload = {
          access_token: session.access_token,
          currentSession: { access_token: session.access_token },
          user: { access_token: session.access_token }
        };
        window.localStorage.setItem(WORKER_AUTH_STORAGE_KEY, JSON.stringify(payload));
        // Пробрасываем токен в Realtime, чтобы WebSocket использовал пользовательский JWT
        supabase.realtime.setAuth(session.access_token)
      } else {
        window.localStorage.removeItem(WORKER_AUTH_STORAGE_KEY);
        // Убираем токен из Realtime при выходе пользователя
        supabase.realtime.setAuth(undefined)
      }
    } catch (e) {
      // не блокируем поток при ошибке синхронизации
      console.warn('[supabase client] Failed to sync auth token to shared storage', e);
    }
  });
} catch (e) {
  console.warn('[supabase client] onAuthStateChange not available or failed to register', e);
}
